import React from "react";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "./resume.css";

const DELIVERED = [
  { ic: "☁", title: "Cloud Migration & Cost Optimization", text: "Architected and executed on-prem to cloud migrations on both Azure and AWS, cutting cloud spend dramatically through serverless, auto-scaling and resource automation." },
  { ic: "AI", title: "AI-Powered Verification Platform", text: "Architected a Generative-AI solution combining OCR and LLM processing in an event-driven, serverless pipeline for high-throughput, fault-tolerant document/label validation." },
  { ic: "▣", title: "Reusable Mobile Framework", text: "Built a reusable React Native framework for iOS & Android with custom cross-platform UI components and automated QA-to-store distribution flows." },
  { ic: "⟳", title: "Legacy Modernization", text: "Migrated legacy components from C++ to C#/.NET, modernized POS/KDS and WPF experiences, and improved accessibility, extensibility and maintainability." },
  { ic: "⇄", title: "Secure System Integration", text: "Designed integration frameworks with RESTful services, API management, identity-provider integration (OAuth/OIDC) and OWASP-aligned security." },
  { ic: "▤", title: "Reusable Templates & Tooling", text: "Delivered enterprise-wide reusable code templates, packages and CI/CD build/release templates that accelerated delivery and standardized quality." }
];

const SKILL_GROUPS = [
  { h: "Languages", tags: ["C#", "TypeScript", "JavaScript", "Python", "C", "C++", "SQL", "HTML", "CSS/SCSS", "PowerShell"] },
  { h: "Frameworks & Platforms", tags: [".NET Core", ".NET Framework", "ASP.NET", "Web API", "MVC", "WPF", "Node.js", "React.js", "React Native", "Angular", "Next.js", "Entity Framework"] },
  { h: "Cloud & DevOps", tags: ["Azure Cloud", "AWS", "Azure DevOps", "AKS / Kubernetes", "Docker", "CI/CD Automation", "Microservices", "Redis Cache", "Performance Tuning"] },
  { h: "AI & Data", tags: ["Artificial Intelligence", "Generative AI", "Data Modernization", "Databases", "Oracle", "RESTful Services"] },
  { h: "Architecture & Practices", tags: ["Solution Architecture", "System Design", "SOLID Principles", "Highly Scalable Apps", "Extensibility", "Security / OWASP", "Unit Testing", "SDLC", "Agile / Scrum"] },
  { h: "Leadership & Delivery", tags: ["Team Leadership", "Mentoring", "Cross-team Collaboration", "Client Communication", "Presales", "Project Management", "Skill Development"] }
];

const CERTS = [
  { t: "Salesforce Platform Developer I", iss: "Salesforce · 2025" },
  { t: "Azure DevOps Engineer Expert", iss: "Microsoft · 2024" },
  { t: "Azure Developer Associate", iss: "Microsoft · 2020" },
  { t: "Azure Data Fundamentals", iss: "Microsoft · 2023" },
  { t: "Azure AI Fundamentals", iss: "Microsoft · 2023" },
  { t: "Career Essentials in Generative AI", iss: "Microsoft & LinkedIn · 2023" },
  { t: "Programming in C#", iss: "Microsoft · 2016" },
  { t: "Pythonic Programming", iss: "Cloud Academy · 2023" }
];

function Resume() {
  return (
    <div className="home-container">
      <Header resume />

      {/* SUMMARY */}
      <main id="summary" className="rhero">
        <div className="container rhero-grid">
          <div>
            <span className="eyebrow"><span className="dot"></span>Résumé · Professional Profile</span>
            <h1>Gayan N. Wimalarathna</h1>
            <div className="role">Engineering Manager · Solution Architect (.NET · React Native · Azure)</div>
            <p>Seasoned Engineering Manager and Account Architect with 15+ years designing, reviewing and delivering enterprise-grade software and technology solutions. I lead architectural vision, technical strategy and end-to-end implementation across multiple engineering teams — ensuring alignment between product goals, delivery milestones and stakeholder expectations. I translate complex operational requirements into scalable, secure and reliable solutions, driving Agile/Scrum and strong cross-functional collaboration.</p>
            <div className="meta">
              <span>◆ Ohio, USA</span>
              <span>◆ 15+ Years</span>
              <span>◆ Enterprise Architecture</span>
              <span>◆ Cloud · AI · DevSecOps</span>
            </div>
            <div className="rhero-cta">
              <a className="btn btn-primary" href="/#contact">Get in Touch →</a>
              <button className="btn btn-ghost" type="button" onClick={() => window.print()}>Print / Save PDF</button>
            </div>
          </div>
          <div className="rstats">
            <div className="rstat"><div className="n">15+</div><div className="l">Years of experience</div></div>
            <div className="rstat"><div className="n">8</div><div className="l">Certifications</div></div>
            <div className="rstat"><div className="n">25+</div><div className="l">Technologies</div></div>
            <div className="rstat"><div className="n">Azure+AWS</div><div className="l">Multi-cloud</div></div>
          </div>
        </div>
      </main>

      {/* EXPERIENCE */}
      <section id="experience" className="block">
        <div className="container">
          <div className="sec-title"><h2>Experience</h2><span className="line"></span></div>
          <div className="card">
            <div className="timeline">
              <div className="tl">
                <div className="top">
                  <h3>Engineering Manager / Architect — Technology</h3>
                  <span className="when">2013 – Present</span>
                </div>
                <ul>
                  <li>Lead architectural vision and technical strategy across multiple engineering teams, owning end-to-end solution design, delivery and quality.</li>
                  <li>Serve as key Cloud Architect on cloud &amp; IT cost-optimization initiatives, driving major reductions in monthly cloud spend through migration, modernization and serverless adoption.</li>
                  <li>Architect secure integration frameworks that connect internal systems with external platforms via encrypted channels and standardized APIs — improving data accuracy, interoperability and operational visibility.</li>
                  <li>Own design, development and maintenance using ASP.NET Web API, MVC, WPF, .NET Core and Node.js with React, Angular and Next.js across multiple projects.</li>
                  <li>Support pre-sales and discovery — solution design, architecture assessments, effort/cost estimation and client engagement strategy.</li>
                  <li>Lead engineering and design teams to meet safety, compliance and code-quality standards, prioritizing risk mitigation and security.</li>
                  <li>Serve on the internal interview board for Cloud, .NET and DevSecOps hiring; run knowledge-sharing sessions and mentor next-level leaders.</li>
                </ul>
              </div>
              <div className="tl">
                <div className="top">
                  <h3>IT Executive</h3>
                  <span className="when">2011 – 2013</span>
                </div>
                <ul>
                  <li>Developed, maintained and enhanced a student program-management system and official portals/websites.</li>
                  <li>Ensured reliability and security of computer labs and equipment; conducted IT audits maintaining integrity and best practices.</li>
                  <li>Introduced new technology solutions and process improvements to raise service quality and reduce IT expenses.</li>
                  <li>Consulted with users, management and vendors to assess computing needs and system requirements.</li>
                </ul>
              </div>
              <div className="tl">
                <div className="top">
                  <h3>Systems Administrator</h3>
                  <span className="when">2009 – 2011</span>
                </div>
                <ul>
                  <li>Established system specifications and designed robust infrastructure across PC/server hardware, LAN/WAN networks and OS management.</li>
                  <li>Ensured optimal performance via monitoring, performance tuning, load/stress testing and troubleshooting.</li>
                  <li>Enhanced security with access-control and evaluation mechanisms; established and tested disaster-recovery policies and backups.</li>
                  <li>Administered Windows and Linux servers with least-privilege user management.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT I'VE DELIVERED */}
      <section className="block">
        <div className="container">
          <div className="sec-title"><h2>What I've Delivered</h2><span className="line"></span></div>
          <p className="lead" style={{ marginBottom: "24px" }}>Representative capabilities and engagement types — described by capability rather than client, to demonstrate the breadth of what I can architect and deliver.</p>
          <div className="grid3">
            {DELIVERED.map((d) => (
              <div className="dcard" key={d.title}>
                <div className="ic">{d.ic}</div>
                <h3>{d.title}</h3>
                <p>{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="block">
        <div className="container">
          <div className="sec-title"><h2>Skills &amp; Expertise</h2><span className="line"></span></div>
          <div className="grid2">
            {SKILL_GROUPS.map((g) => (
              <div className="sgroup" key={g.h}>
                <h3>{g.h}</h3>
                <div className="tags">
                  {g.tags.map((t) => (
                    <span className="tag2" key={t}>{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section id="certs" className="block">
        <div className="container">
          <div className="sec-title"><h2>Certifications</h2><span className="line"></span></div>
          <div className="certs">
            {CERTS.map((c) => (
              <div className="cert" key={c.t}>
                <div className="badge">✓</div>
                <div>
                  <h4>{c.t}</h4>
                  <div className="iss">{c.iss}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATION */}
      <section className="block">
        <div className="container">
          <div className="sec-title"><h2>Education</h2><span className="line"></span></div>
          <div className="card edu">
            <div className="cap">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M12 4 22 9 12 14 2 9 12 4Z" stroke="#F59E0B" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" stroke="#F97316" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h3>BSc. (Hons) in Information Technology</h3>
              <p>Sri Lanka Institute of Information Technology · 2012</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Resume;
